import { resolveOtherServiceCustomerAmounts } from '../../utils/otherServicePricing';

const toCents = (value) => Math.round((Number(value) || 0) * 100);
const toRate = (value) => {
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return rate > 1 ? rate / 100 : rate;
};

/**
 * Infer a common rate only when every customer-facing product and service
 * is demonstrably taxable at that same rate. Used for the live Sales Order
 * and invoice-owned cart summaries. Mixed rates, exemptions and incomplete
 * amounts return null rather than guessing a rate.
 */
export const inferUniformSalesOrderCartTaxRate = ({
  transformedTotals = [], services = [], subtotalCents,
} = {}) => {
  const products = Array.isArray(transformedTotals) ? transformedTotals : [];
  const otherServices = Array.isArray(services) ? services : [];
  if (!products.length && !otherServices.length) return null;

  const rates = [];
  let taxableCents = 0;
  for (const row of products) {
    const breakdown = row?.taxBreakdown;
    if (!Array.isArray(breakdown) || breakdown.length !== 1) return null;
    const tax = breakdown[0];
    const rate = toRate(tax?.rate);
    const amount = Number(tax?.taxableAmount);
    const rowSubtotal = Number(row?.amount) - Number(row?.taxAmount);
    if (!rate || !Number.isFinite(amount) || amount <= 0 ||
        !Number.isFinite(rowSubtotal) ||
        Math.abs(toCents(rowSubtotal) - toCents(amount)) > 1) return null;
    taxableCents += toCents(amount);
    rates.push(rate);
  }
  for (const service of otherServices) {
    const amounts = resolveOtherServiceCustomerAmounts(service);
    const subtotal = toCents(amounts.subtotal);
    if (subtotal <= 0) continue;
    const rate = toRate(amounts.taxRate);
    if (!rate) return null;
    taxableCents += subtotal;
    rates.push(rate);
  }
  if (!rates.length || rates.some((rate) => Math.abs(rate - rates[0]) > 1e-8)) return null;
  if (taxableCents !== Number(subtotalCents)) return null;
  return rates[0];
};

/**
 * Read-only Cart Summary tax reconciliation.
 * Active Sales Order Cart totals must sum already-rounded product and service
 * tax lines, matching generated Sales Orders and server snapshot invoice totals.
 * Keep existing Order Cart and invoice-owned cart rounding policies unchanged:
 * historical, deposit, partial and balance invoices must not be repriced by
 * a Sales Order display correction.
 */
export const resolveCartSummaryTaxCents = ({
  source = 'order-cart',
  transformedTotals = [],
  services = [],
  subtotalCents = 0,
  rowTaxCents = 0,
  configuredRate = 0,
  adjustments = [],
  invoiceRole = '',
} = {}) => {
  const role = String(invoiceRole || '').trim().toLowerCase();
  const isInvoice = source === 'invoicing-cart';
  const isSO = source === 'sales-order-cart';
  const hasAdjustments = Array.isArray(adjustments) && adjustments.length > 0;
  const isPartialInvoice = [
    'deposit', 'partial', 'partial-invoice', 'progress', 'progress-invoice',
    'balance', 'balance-invoice',
  ].includes(role);

  const eligibleForUniformRate = isSO ||
    (isInvoice && role === 'full' && !hasAdjustments && !isPartialInvoice);
  const inferredRate = eligibleForUniformRate
    ? inferUniformSalesOrderCartTaxRate({ transformedTotals, services, subtotalCents })
    : null;
  const fallback = Number(configuredRate);
  const effectiveRate = inferredRate == null
    ? (Number.isFinite(fallback) && fallback > 0 ? fallback : 0)
    : inferredRate;
  const taxableCents = Number(subtotalCents) || 0;
  const lineTaxCents = Number(rowTaxCents) || 0;
  const taxFromSubtotalCents = Math.round(taxableCents * effectiveRate);
  const taxDeltaCents = Math.abs(taxFromSubtotalCents - lineTaxCents);

  // Generated Sales Orders and the server's snapshot invoiceable calculation
  // sum tax rounded at product/service level. Do not silently replace their
  // sum with tax rounded once on the whole subtotal, even for a 1-cent delta.
  // Keep the historical Order Cart and invoice-owned cart paths untouched.
  const canReconcile = !isInvoice || inferredRate != null;
  const chosenTaxCents = isSO
    ? lineTaxCents
    : canReconcile && taxDeltaCents <= 1
      ? taxFromSubtotalCents
      : lineTaxCents;

  return { chosenTaxCents, taxFromSubtotalCents, taxDeltaCents, inferredRate, effectiveRate };
};
